import React, { useState } from 'react';
import { 
  Code, Plus, X, ChevronDown, ChevronUp, Play, 
  Pause, Save, Activity, DollarSign, TrendingUp, 
  TrendingDown, AlertTriangle, Edit2, Settings
} from 'lucide-react';
import { Token } from '../../types/supabase';

// Define rule types
type Condition = 'above' | 'below' | 'crosses-above' | 'crosses-below' | 'increases-by' | 'decreases-by';
type Indicator = 'price' | 'volume' | 'market-cap' | 'rsi' | 'macd' | 'ma-50' | 'ma-200';
type Action = 'buy' | 'sell';

interface Rule {
  id: string;
  name: string;
  enabled: boolean;
  conditions: {
    id: string;
    indicator: Indicator;
    condition: Condition;
    value: number;
  }[];
  action: {
    type: Action;
    amount: string; // Can be fixed amount or percentage
    amountType: 'fixed' | 'percent';
  };
  token?: Token;
  status?: 'idle' | 'running' | 'triggered' | 'error';
}

interface RuleBasedTraderProps {
  selectedToken?: Token;
  onClose?: () => void;
  isWidget?: boolean;
}

// Define CheckBox icon component outside the main component
const CheckBoxIcon = ({ size = 24, className = "" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);

const RuleBasedTrader: React.FC<RuleBasedTraderProps> = ({ 
  selectedToken,
  onClose, 
  isWidget = false 
}) => {
  const [rules, setRules] = useState<Rule[]>([
    {
      id: '1',
      name: 'Buy on dip',
      enabled: true,
      conditions: [
        {
          id: 'c1',
          indicator: 'price',
          condition: 'decreases-by',
          value: 5
        },
        {
          id: 'c2',
          indicator: 'volume',
          condition: 'above',
          value: 100000
        }
      ],
      action: {
        type: 'buy',
        amount: '100',
        amountType: 'fixed'
      },
      token: selectedToken,
      status: 'running'
    },
    {
      id: '2',
      name: 'Sell on pump',
      enabled: false,
      conditions: [
        {
          id: 'c3',
          indicator: 'price',
          condition: 'increases-by',
          value: 10
        }
      ],
      action: {
        type: 'sell',
        amount: '50',
        amountType: 'percent'
      },
      token: selectedToken,
      status: 'idle'
    }
  ]);
  
  const [editingRuleId, setEditingRuleId] = useState<string | null>(null);
  const [newRule, setNewRule] = useState<Rule | null>(null);
  const [activeTab, setActiveTab] = useState<'my-rules' | 'templates'>('my-rules');
  
  // Indicators with their display names and default values
  const indicators = [
    { value: 'price', label: 'Price', icon: <DollarSign size={12} /> },
    { value: 'volume', label: 'Volume', icon: <Activity size={12} /> },
    { value: 'market-cap', label: 'Market Cap', icon: <DollarSign size={12} /> },
    { value: 'rsi', label: 'RSI', icon: <Activity size={12} /> },
    { value: 'macd', label: 'MACD', icon: <Activity size={12} /> },
    { value: 'ma-50', label: '50 MA', icon: <TrendingUp size={12} /> },
    { value: 'ma-200', label: '200 MA', icon: <TrendingUp size={12} /> }
  ];
  
  // Conditions with their display names
  const conditions = [
    { value: 'above', label: 'is above' },
    { value: 'below', label: 'is below' },
    { value: 'crosses-above', label: 'crosses above' },
    { value: 'crosses-below', label: 'crosses below' },
    { value: 'increases-by', label: 'increases by' },
    { value: 'decreases-by', label: 'decreases by' }
  ];
  
  // Create a new rule
  const handleCreateRule = () => {
    const newId = Math.random().toString(36).substring(2, 9);
    const rule: Rule = {
      id: newId,
      name: 'New Rule',
      enabled: false,
      conditions: [
        {
          id: `c-${newId}-1`,
          indicator: 'price',
          condition: 'above',
          value: 0
        }
      ],
      action: {
        type: 'buy',
        amount: '100',
        amountType: 'fixed'
      },
      token: selectedToken,
      status: 'idle'
    };
    
    setNewRule(rule);
    setEditingRuleId(rule.id);
  };
  
  // Save a rule (new or edited)
  const handleSaveRule = () => {
    if (newRule) {
      if (rules.find(r => r.id === newRule.id)) {
        // Update existing rule
        setRules(rules.map(r => r.id === newRule.id ? newRule : r));
      } else {
        // Add new rule
        setRules([...rules, newRule]);
      }
      setNewRule(null);
      setEditingRuleId(null);
    }
  };
  
  // Cancel rule creation/editing
  const handleCancelEdit = () => {
    setNewRule(null);
    setEditingRuleId(null);
  };
  
  // Edit an existing rule
  const handleEditRule = (ruleId: string) => {
    const ruleToEdit = rules.find(r => r.id === ruleId);
    if (ruleToEdit) {
      setNewRule({ ...ruleToEdit });
      setEditingRuleId(ruleId);
    }
  };
  
  // Delete a rule
  const handleDeleteRule = (ruleId: string) => {
    setRules(rules.filter(r => r.id !== ruleId));
    if (editingRuleId === ruleId) {
      setNewRule(null);
      setEditingRuleId(null);
    }
  };
  
  // Toggle rule enabled state
  const handleToggleRuleEnabled = (ruleId: string) => {
    setRules(rules.map(rule => 
      rule.id === ruleId ? { ...rule, enabled: !rule.enabled } : rule
    ));
  };
  
  // Add a condition to the rule being edited
  const handleAddCondition = () => {
    if (newRule) {
      const newConditionId = `c-${newRule.id}-${newRule.conditions.length + 1}`;
      setNewRule({
        ...newRule,
        conditions: [
          ...newRule.conditions,
          {
            id: newConditionId,
            indicator: 'price',
            condition: 'above',
            value: 0
          }
        ]
      });
    }
  };
  
  // Remove a condition from the rule being edited
  const handleRemoveCondition = (conditionId: string) => {
    if (newRule) {
      setNewRule({
        ...newRule,
        conditions: newRule.conditions.filter(c => c.id !== conditionId)
      });
    }
  };
  
  // Update a condition in the rule being edited
  const handleUpdateCondition = (conditionId: string, field: string, value: any) => {
    if (newRule) {
      setNewRule({
        ...newRule,
        conditions: newRule.conditions.map(condition => 
          condition.id === conditionId 
            ? { ...condition, [field]: value } 
            : condition
        )
      });
    }
  };
  
  // Update action in the rule being edited
  const handleUpdateAction = (field: string, value: any) => {
    if (newRule) {
      setNewRule({
        ...newRule,
        action: { 
          ...newRule.action, 
          [field]: value 
        }
      });
    }
  };
  
  // Render the rule editor
  const renderRuleEditor = () => {
    if (!newRule) return null;
    
    return (
      <div className="p-4 border-t border-white/10">
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Rule Name</label>
          <input
            type="text"
            value={newRule.name}
            onChange={(e) => setNewRule({ ...newRule, name: e.target.value })}
            className="w-full p-2 bg-[#1a2a2a] border border-gray-700/50 rounded-lg text-sm"
          />
        </div>
        
        <div className="mb-4">
          <div className="flex justify-between items-center mb-2">
            <label className="text-sm font-medium">Conditions</label>
            <button
              onClick={handleAddCondition}
              className="text-xs flex items-center text-[#47be8f]"
            >
              <Plus size={12} className="mr-1" /> Add Condition
            </button>
          </div>
          
          {newRule.conditions.map((condition, index) => (
            <div key={condition.id} className="mb-3 p-3 bg-[#1a2a2a] border border-gray-700/30 rounded-lg">
              <div className="flex justify-between items-center mb-2">
                <label className="text-xs font-medium">Condition {index + 1}</label>
                {newRule.conditions.length > 1 && (
                  <button
                    onClick={() => handleRemoveCondition(condition.id)}
                    className="text-xs text-red-400"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
              
              <div className="flex gap-2 mb-2">
                <select
                  value={condition.indicator}
                  onChange={(e) => handleUpdateCondition(condition.id, 'indicator', e.target.value as Indicator)}
                  className="flex-1 p-2 bg-[#1a2a2a] border border-gray-700/50 rounded-lg text-xs"
                >
                  {indicators.map(ind => (
                    <option key={ind.value} value={ind.value}>
                      {ind.label}
                    </option>
                  ))}
                </select>
                
                <select
                  value={condition.condition}
                  onChange={(e) => handleUpdateCondition(condition.id, 'condition', e.target.value as Condition)}
                  className="flex-1 p-2 bg-[#1a2a2a] border border-gray-700/50 rounded-lg text-xs"
                >
                  {conditions.map(cond => (
                    <option key={cond.value} value={cond.value}>
                      {cond.label}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <input
                  type="text"
                  value={condition.value}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '' || /^\d*\.?\d*$/.test(value)) {
                      handleUpdateCondition(condition.id, 'value', parseFloat(value) || 0);
                    }
                  }}
                  className="w-full p-2 bg-[#1a2a2a] border border-gray-700/50 rounded-lg text-xs"
                  placeholder="Value"
                  inputMode="decimal"
                />
              </div>
            </div>
          ))}
        </div>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2">Action</label>
          <div className="flex gap-2 mb-2">
            <select
              value={newRule.action.type}
              onChange={(e) => handleUpdateAction('type', e.target.value as Action)}
              className="flex-1 p-2 bg-[#1a2a2a] border border-gray-700/50 rounded-lg text-sm"
            >
              <option value="buy">Buy</option>
              <option value="sell">Sell</option>
            </select>
            
            <div className="relative flex-1">
              <input
                type="text"
                value={newRule.action.amount}
                onChange={(e) => handleUpdateAction('amount', e.target.value)}
                className="w-full p-2 bg-[#1a2a2a] border border-gray-700/50 rounded-lg text-sm"
              />
              <select
                value={newRule.action.amountType}
                onChange={(e) => handleUpdateAction('amountType', e.target.value as 'fixed' | 'percent')}
                className="absolute right-0 top-0 h-full border-l border-gray-700/50 bg-[#1a2a2a] rounded-r-lg text-xs px-1"
              >
                <option value="fixed">USD</option>
                <option value="percent">%</option>
              </select>
            </div>
          </div>
        </div>
        
        <div className="flex gap-2 justify-end">
          <button
            onClick={handleCancelEdit}
            className="px-3 py-1 bg-gray-700/50 text-white rounded-lg text-sm"
          >
            Cancel
          </button>
          <button
            onClick={handleSaveRule}
            className="px-3 py-1 bg-[#47be8f] text-black rounded-lg text-sm"
          >
            Save Rule
          </button>
        </div>
      </div>
    );
  };
  
  // Get status icon and color for a rule
  const getRuleStatusInfo = (status?: string) => {
    switch(status) {
      case 'running':
        return { icon: <Play size={14} className="text-green-400" />, color: 'text-green-400' };
      case 'triggered':
        return { icon: <CheckBoxIcon size={14} className="text-blue-400" />, color: 'text-blue-400' };
      case 'error':
        return { icon: <AlertTriangle size={14} className="text-red-400" />, color: 'text-red-400' };
      default: // 'idle'
        return { icon: <Pause size={14} className="text-gray-400" />, color: 'text-gray-400' };
    }
  };
  
  const customStyles = `
    .tab-button {
      padding: 0.5rem 1rem;
      font-size: 0.875rem;
      border-bottom: 2px solid transparent;
      transition: all 0.2s;
    }
    
    .tab-button.active {
      border-bottom-color: #47be8f;
      color: #47be8f;
    }
    
    .rule-item {
      background-color: rgba(24, 40, 40, 0.6);
      border: 1px solid rgba(255, 255, 255, 0.1);
      transition: all 0.2s;
    }
    
    .rule-item:hover {
      background-color: rgba(24, 40, 40, 0.8);
      border-color: rgba(71, 190, 143, 0.3);
    }
  `;
  
  const content = (
    <div className="w-full h-full flex flex-col bg-[#16161a] border border-[#24c35b] rounded-lg shadow-lg" data-widget-type="rule-based-trader">
      <style>{customStyles}</style>
      {/* Header */}
      <div className="p-2 border-b border-[#24c35b] bg-[#16161a] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Settings size={18} className="text-[#24c35b]" />
          <h2 className="text-lg font-bold text-white">Rule Based Trader</h2>
        </div>
        {onClose && (
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors p-1 rounded hover:bg-gray-700/20"
            title="Close"
          >
            <X size={16} />
          </button>
        )}
      </div>
      
      {/* Tab Navigation */}
      <div className="flex border-b border-white/10">
        <button
          className={`tab-button ${activeTab === 'my-rules' ? 'active' : ''}`}
          onClick={() => setActiveTab('my-rules')}
        >
          My Rules
        </button>
        <button
          className={`tab-button ${activeTab === 'templates' ? 'active' : ''}`}
          onClick={() => setActiveTab('templates')}
        >
          Templates
        </button>
      </div>
      
      {/* Main content depends on tab */}
      {activeTab === 'my-rules' && (
        <div className="flex-1 overflow-y-auto">
          {/* Rules List */}
          {rules.length === 0 ? (
            <div className="h-full flex items-center justify-center text-center p-4">
              <div>
                <Code size={32} className="mx-auto mb-2 text-gray-400" />
                <p className="text-sm text-gray-400">No rules created yet</p>
                <button
                  className="mt-2 text-sm text-[#47be8f] hover:underline"
                  onClick={handleCreateRule}
                >
                  Create your first rule
                </button>
              </div>
            </div>
          ) : (
            <div className="p-3 space-y-3">
              {rules.map(rule => (
                <div 
                  key={rule.id} 
                  className={`rule-item rounded-lg p-3 ${editingRuleId === rule.id ? 'border-[#47be8f]/40' : ''}`}
                >
                  <div className="flex justify-between items-center mb-2">
                    <div className="flex items-center">
                      <div
                        className={`w-3 h-3 rounded-full mr-2 ${rule.enabled ? 'bg-green-400' : 'bg-gray-500'}`}
                      ></div>
                      <h4 className="text-sm font-medium">{rule.name}</h4>
                      {rule.status && (
                        <div className="ml-2 flex items-center">
                          {getRuleStatusInfo(rule.status).icon}
                        </div>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleEditRule(rule.id)}
                        className="p-1 hover:text-[#47be8f]"
                      >
                        <Edit2 size={14} />
                      </button>
                      <button
                        onClick={() => handleToggleRuleEnabled(rule.id)}
                        className={`p-1 ${rule.enabled ? 'text-green-400 hover:text-green-500' : 'text-gray-500 hover:text-white'}`}
                      >
                        {rule.enabled ? <Pause size={14} /> : <Play size={14} />}
                      </button>
                      <button
                        onClick={() => handleDeleteRule(rule.id)}
                        className="p-1 text-gray-500 hover:text-red-400"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  </div>
                  
                  {rule.token && (
                    <div className="mb-2 text-xs">
                      <span className="text-gray-400">Token: </span>
                      <span>{rule.token.symbol || 'Unknown'}</span>
                    </div>
                  )}
                  
                  <div className="text-xs space-y-1">
                    {rule.conditions.map((condition, i) => {
                      const indicatorObj = indicators.find(ind => ind.value === condition.indicator);
                      const conditionObj = conditions.find(cond => cond.value === condition.condition);
                      return (
                        <div key={condition.id} className="flex items-center">
                          {i > 0 && <span className="mr-2 text-gray-400">AND</span>}
                          <span className="flex items-center">
                            {indicatorObj?.icon} 
                            <span className="ml-1">{indicatorObj?.label || condition.indicator}</span>
                          </span>
                          <span className="mx-1 text-gray-400">{conditionObj?.label || condition.condition}</span>
                          <span>{condition.value}</span>
                        </div>
                      );
                    })}
                  </div>
                  
                  <div className="mt-2 pt-2 border-t border-white/10 text-xs flex items-center">
                    <div className={`mr-2 ${rule.action.type === 'buy' ? 'text-green-400' : 'text-red-400'}`}>
                      {rule.action.type === 'buy' ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                    </div>
                    <span className={rule.action.type === 'buy' ? 'text-green-400' : 'text-red-400'}>
                      {rule.action.type === 'buy' ? 'Buy' : 'Sell'} {rule.action.amount}{rule.action.amountType === 'percent' ? '%' : ' USD'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {/* Rule Editor - shown when editing or creating a rule */}
          {editingRuleId && renderRuleEditor()}
          
          {/* Add Rule Button - shown when not editing */}
          {!editingRuleId && (
            <div className="p-3 border-t border-white/10">
              <button
                onClick={handleCreateRule}
                className="w-full py-2 flex items-center justify-center bg-[#1a2a2a] hover:bg-[#1c2c2c] border border-gray-700/50 rounded-lg text-sm"
              >
                <Plus size={16} className="mr-2" /> Create New Rule
              </button>
            </div>
          )}
        </div>
      )}
      
      {activeTab === 'templates' && (
        <div className="flex-1 overflow-y-auto p-3">
          <div className="mb-3 text-sm">Popular strategy templates</div>
          <div className="space-y-3">
            <div className="rule-item rounded-lg p-3 hover:cursor-pointer">
              <div className="flex justify-between items-center">
                <h4 className="text-sm font-medium">Buy the Dip</h4>
                <Plus size={14} className="text-[#47be8f]" />
              </div>
              <p className="text-xs text-gray-400 mt-1">Buy when price drops by specified percentage with volume confirmation</p>
            </div>
            <div className="rule-item rounded-lg p-3 hover:cursor-pointer">
              <div className="flex justify-between items-center">
                <h4 className="text-sm font-medium">MA Crossover</h4>
                <Plus size={14} className="text-[#47be8f]" />
              </div>
              <p className="text-xs text-gray-400 mt-1">Buy when 50 MA crosses above 200 MA (Golden Cross)</p>
            </div>
            <div className="rule-item rounded-lg p-3 hover:cursor-pointer">
              <div className="flex justify-between items-center">
                <h4 className="text-sm font-medium">RSI Oversold</h4>
                <Plus size={14} className="text-[#47be8f]" />
              </div>
              <p className="text-xs text-gray-400 mt-1">Buy when RSI dips below 30 (oversold condition)</p>
            </div>
            <div className="rule-item rounded-lg p-3 hover:cursor-pointer">
              <div className="flex justify-between items-center">
                <h4 className="text-sm font-medium">Take Profit Strategy</h4>
                <Plus size={14} className="text-[#47be8f]" />
              </div>
              <p className="text-xs text-gray-400 mt-1">Sell 50% when price increases by 20%, sell remaining at 50%</p>
            </div>
          </div>
        </div>  
      )}
    </div>
  );
  
  // If this is being rendered as a widget, return just the content
  if (isWidget) {
    return content;
  }
  
  // Otherwise, return the modal version with container
  return (
    <div className="p-4 bg-dark-grey border border-gray-800 rounded-lg">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Code size={18} className="text-primary" />
          <h2 className="text-lg font-bold text-white">Rule Based Trader</h2>
        </div>
        {onClose && (
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-white"
          >
            &times;
          </button>
        )}
      </div>
      
      {content}
    </div>
  );
};

// Factory function to create a canvas widget - moved to top level
export const createRuleBasedTraderWidget = () => {
  return {
    type: 'rule-based-trader',
    title: 'Rule Based Trader',
    content: <RuleBasedTrader isWidget={true} />,
    size: { width: 450, height: 600 },
    padding: 0,
    margin: 0,
    contentStyle: { 
      padding: 0, 
      margin: 0,
      backgroundColor: 'transparent',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      overflow: 'hidden'
    },
    disablePadding: true,
    fullWidth: true,
    fullHeight: true,
    background: 'transparent',
    glassMorphism: true,
    containerClassName: 'p-0 m-0 overflow-hidden',
    containerStyle: {
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(12px)',
      borderRadius: '12px',
      border: '1px solid rgba(36, 195, 91, 0.2)',
      boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15), 0 4px 16px rgba(36, 195, 91, 0.2)',
      overflow: 'hidden'
    }
  };
};

export default RuleBasedTrader;
